import React, { useCallback, useEffect, useState } from "react";

// Approval Queue — the human sign-off surface Jason asked for: "plan a month
// ahead, precheck, write, check, approve, then schedule green." Every post the
// pipeline WROTE and QA-passed lands here as pending_approval and publishes only
// after a human approves it. Nothing turns green on the schedule without a
// decision here (Ashley today, Perkins once built).
//
// Backend: GET /api/approvals/queue, POST /api/approvals/:id/approve,
// POST /api/approvals/:id/reject { reason }, GET /api/approvals/failures.
//
// Props:
//   api            — backend base URL
//   authFetch      — parent's auth-injecting fetch wrapper
//   onCountChange  — optional; called with the pending count for the nav badge
export default function ApprovalQueue({ api, authFetch, onCountChange }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [clientFilter, setClientFilter] = useState("");

  const flash = (kind, text, ms = 4500) => { setToast({ kind, text }); setTimeout(() => setToast(null), ms); };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await authFetch(`${api}/api/approvals/queue`);
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.posts) ? data.posts : [];
      setPosts(list);
      if (typeof onCountChange === "function") onCountChange(list.length);
    } catch (e) {
      setError(e?.message || String(e)); setPosts([]);
      if (typeof onCountChange === "function") onCountChange(0);
    } finally { setLoading(false); }
  }, [api, authFetch, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (post) => {
    if (busyId) return;
    setBusyId(post.id);
    try {
      const res = await authFetch(`${api}/api/approvals/${post.id}/approve`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { flash("err", `Approve failed: ${data?.error || `HTTP ${res.status}`}`); return; }
      flash("ok", `Approved "${post.title || post.keyword}". It will publish on its next scheduled slot.`);
      await load();
    } catch (e) { flash("err", `Approve threw: ${e?.message || e}`); }
    finally { setBusyId(null); }
  };

  const reject = async (post) => {
    if (busyId) return;
    const reason = window.prompt(
      "Why is this post being rejected? The reason is logged and fed into the rewrite so it does not fail the same way.\n\n(e.g. \"internal link to the running-toilet post is off-topic for a faucet aerator article\")"
    );
    if (reason == null) return; // cancelled
    if (!reason.trim()) { flash("err", "A reason is required to reject."); return; }
    setBusyId(post.id);
    try {
      const res = await authFetch(`${api}/api/approvals/${post.id}/reject`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { flash("err", `Reject failed: ${data?.error || `HTTP ${res.status}`}`); return; }
      const rw = data?.rewrite;
      flash("ok",
        rw === "regenerated" ? "Rejected, logged, and rewritten using that reason — the new draft is back in this queue."
          : rw === "decision_required" ? "Rejected + logged. Rewrite needs a current accepted content decision (re-plan the topic)."
            : rw === "failed" ? `Rejected + logged. Auto-rewrite did not clear: ${data?.detail || "see failure log"}.`
              : "Rejected and logged.");
      await load();
    } catch (e) { flash("err", `Reject threw: ${e?.message || e}`); }
    finally { setBusyId(null); }
  };

  if (loading) return <div style={{ color: "#888", padding: 40, textAlign: "center" }}>Loading approval queue…</div>;

  const clientOptions = Array.from(new Set(posts.map((p) => p?.client?.name).filter(Boolean))).sort();
  const visible = posts.filter((p) => !clientFilter || (p?.client?.name || "") === clientFilter);

  return (
    <div>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>Approval</div>
          <div style={styles.headerCount}>{posts.length} post{posts.length === 1 ? "" : "s"} awaiting approval</div>
          <div style={styles.headerSub}>
            Each post was planned, pre-checked, written and QA-scored. Nothing publishes until you approve it here. Reject records why and rewrites it so it does not fail the same way.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {clientOptions.length > 1 && (
            <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} style={styles.clientSelect}>
              <option value="">All clients</option>
              {clientOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button onClick={load} style={styles.refreshBtn}>↻ Refresh</button>
        </div>
      </div>

      {error && <div style={styles.errorBanner}><strong>Failed to load queue.</strong> {error}</div>}
      {toast && (
        <div style={{ ...styles.toast, background: toast.kind === "ok" ? "rgba(34,197,94,0.15)" : "rgba(214,0,0,0.15)", borderColor: toast.kind === "ok" ? "#22C55E" : "#D60000", color: toast.kind === "ok" ? "#86efac" : "#fca5a5" }}>
          {toast.text}
        </div>
      )}

      {posts.length === 0 && !error && (
        <div style={styles.empty}>
          <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }}>✓</div>
          <div>Nothing awaiting approval. Every written post has been decided.</div>
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {visible.map((post) => {
          const c = post.checks || {};
          const isExpanded = expandedId === post.id;
          const isBusy = busyId === post.id;
          const scoreTone = c.qa_score == null ? "#888" : c.qa_score >= 85 ? "#22C55E" : c.qa_score >= 70 ? "#EAB308" : "#D60000";
          return (
            <div key={post.id} style={styles.card}>
              <div style={styles.cardHead}>
                <span style={{ ...styles.scorePill, color: scoreTone, borderColor: scoreTone }}>
                  QA {c.qa_score == null ? "—" : c.qa_score}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.title}>{post.title || <em style={{ color: "#555" }}>no title</em>}</div>
                  <div style={styles.meta}>
                    {(post.client?.name || "—")}{post.client?.industry ? ` · ${post.client.industry}` : ""} · {post.keyword} · {new Date(post.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => setExpandedId(isExpanded ? null : post.id)} style={styles.expandBtn} disabled={isBusy}>
                  {isExpanded ? "Collapse" : "Details"}
                </button>
              </div>

              <div style={styles.flags}>
                <Flag ok={c.qa_passed} label={c.qa_passed ? "QA passed" : "QA failed"} />
                <Flag ok={!c.over_word_cap} label={c.over_word_cap ? `${c.word_count}w · over 1500` : `${c.word_count}w`} />
                <Flag ok={!c.has_em_dash} label={c.has_em_dash ? "em-dash present" : "no em-dash"} />
                <Flag ok={c.has_meta && c.meta_len <= 140} label={`meta ${c.meta_len}`} />
                <Flag ok={c.issue_count === 0} label={c.issue_count === 0 ? "0 issues" : `${c.issue_count} issue${c.issue_count === 1 ? "" : "s"}`} />
                {c.warning_count > 0 && <Flag ok={null} label={`${c.warning_count} warn`} />}
              </div>

              {isExpanded && (
                <div style={styles.expanded}>
                  {(c.issues?.length > 0 || c.warnings?.length > 0) && (
                    <>
                      <div style={styles.sectionLabel}>Check results</div>
                      <ul style={styles.issueList}>
                        {(c.issues || []).map((i, n) => <li key={`i${n}`} style={{ color: "#fca5a5" }}>{i.type ? `${i.type}: ` : ""}{i.message || JSON.stringify(i)}</li>)}
                        {(c.warnings || []).map((w, n) => <li key={`w${n}`} style={{ color: "#EAB308" }}>{w.type ? `${w.type}: ` : ""}{w.message || JSON.stringify(w)}</li>)}
                      </ul>
                    </>
                  )}
                  <div style={styles.sectionLabel}>Preview</div>
                  <div style={styles.preview}>
                    <div><strong style={{ color: "#888" }}>Slug:</strong> {post.slug || "—"}</div>
                    <div><strong style={{ color: "#888" }}>Meta:</strong> {post.meta_description || "—"}</div>
                    {post.content && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: "pointer", color: "#888" }}>Body ({post.content.length} chars)</summary>
                        <div style={styles.body} dangerouslySetInnerHTML={{ __html: post.content }} />
                      </details>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.actionRow}>
                <button onClick={() => approve(post)} style={styles.approveBtn} disabled={isBusy}>
                  {isBusy ? "Working…" : "Approve → schedule"}
                </button>
                <button onClick={() => reject(post)} style={styles.rejectBtn} disabled={isBusy}>
                  Reject + rewrite
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Flag({ ok, label }) {
  const color = ok === null ? "#EAB308" : ok ? "#22C55E" : "#D60000";
  return (
    <span style={{ ...flagStyle, color, borderColor: color }}>
      {ok === null ? "•" : ok ? "✓" : "✕"} {label}
    </span>
  );
}

const flagStyle = {
  fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase",
  fontFamily: "'Barlow Condensed', sans-serif", border: "1px solid",
  borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
};

const styles = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 },
  eyebrow: { fontSize: 10, color: "#555", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" },
  headerCount: { fontSize: 24, color: "#fff", fontWeight: 600, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.02em" },
  headerSub: { fontSize: 12, color: "#777", maxWidth: 680, marginTop: 4, lineHeight: 1.5 },
  refreshBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#999", padding: "6px 14px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif" },
  clientSelect: { background: "#070707", border: "1px solid #2a2a2a", color: "#aaa", padding: "5px 10px", fontSize: 11, borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", minWidth: 160, cursor: "pointer" },
  empty: { padding: "60px 20px", textAlign: "center", color: "#666", border: "1px dashed #1a1a1a", borderRadius: 4 },
  errorBanner: { padding: 12, border: "1px solid rgba(214,0,0,0.4)", background: "rgba(214,0,0,0.08)", color: "#fca5a5", borderRadius: 4, marginBottom: 12, fontSize: 13 },
  toast: { padding: "10px 14px", borderRadius: 4, border: "1px solid", marginBottom: 12, fontSize: 13 },
  card: { background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 4, padding: 14 },
  cardHead: { display: "flex", alignItems: "center", gap: 12 },
  scorePill: { border: "1px solid", borderRadius: 3, padding: "3px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.06em", whiteSpace: "nowrap" },
  title: { color: "#fff", fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  meta: { fontSize: 11, color: "#666", marginTop: 2 },
  expandBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#888", padding: "4px 12px", fontSize: 11, cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase" },
  flags: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },
  expanded: { marginTop: 14, paddingTop: 14, borderTop: "1px solid #1a1a1a" },
  sectionLabel: { fontSize: 10, color: "#555", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 6, marginTop: 8 },
  issueList: { margin: "0 0 8px 0", paddingLeft: 18, fontSize: 12, lineHeight: 1.6 },
  preview: { background: "#070707", border: "1px solid #1a1a1a", borderRadius: 3, padding: 10, fontSize: 12, color: "#aaa", display: "grid", gap: 4 },
  body: { background: "#050505", padding: 10, borderRadius: 3, fontSize: 12, color: "#bbb", maxHeight: 360, overflow: "auto", marginTop: 6, lineHeight: 1.6 },
  actionRow: { display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" },
  approveBtn: { background: "#15803d", border: "none", color: "#fff", padding: "8px 18px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600 },
  rejectBtn: { background: "transparent", border: "1px solid rgba(214,0,0,0.4)", color: "#fca5a5", padding: "8px 18px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif" },
};
